import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/urlHelper';
import { FirebaseService } from '../services/firebaseService';

const OnboardingModal = ({ isOpen, onClose, onComplete, clientData }) => {
  const [form, setForm] = useState({
    agentName: '',
    agentVoice: 'alloy',
    firstMessage: '',
    systemPrompt: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Vapi voice options
  const voiceOptions = [
    { value: 'alloy', label: 'Alloy (Neutral)' },
    { value: 'echo', label: 'Echo (Male)' },
    { value: 'fable', label: 'Fable (British)' },
    { value: 'onyx', label: 'Onyx (Deep)' },
    { value: 'nova', label: 'Nova (Female)' },
    { value: 'shimmer', label: 'Shimmer (Soft)' }
  ];

  // Default first message based on agent name
  const getDefaultFirstMessage = (name) => {
    return `Hello! I'm ${name}, your AI assistant. How can I help you today?`;
  };

  // Create agent data when modal opens
  useEffect(() => {
    if (isOpen) {
      
      // Auto-fill form with default values
      setForm({
        agentName: 'Appy',
        agentVoice: 'alloy',
        firstMessage: 'Hello! I\'m Appy, your AI assistant. How can I help you today?',
        systemPrompt: 'You are Appy, the official AI assistant for AppifyAI - a platform that helps businesses deploy AI voice agents.'
      });
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.agentName.trim()) newErrors.agentName = 'Assistant name is required';
    if (!form.firstMessage.trim()) newErrors.firstMessage = 'First message is required';
    if (!form.systemPrompt.trim()) newErrors.systemPrompt = 'Assistant description is required';
    else if (form.systemPrompt.length < 10) {
      newErrors.systemPrompt = 'Please describe what your assistant should help with (at least 10 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-generate first message when agent name changes
  const handleAgentNameChange = (e) => {
    const name = e.target.value;
    setForm(prev => ({ 
      ...prev, 
      agentName: name,
      firstMessage: name ? getDefaultFirstMessage(name) : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const API_URL = getApiUrl();
      
      
      // Check if clientData is available, fallback to localStorage
      
      let currentClientData = clientData;
      if (!currentClientData || !currentClientData.id) {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        
        if (storedUser && storedUser.id) {
          currentClientData = storedUser;
        } else {
          throw new Error('Client data not available. Please try signing up again.');
        }
      }
      
      const response = await fetch(`${API_URL}/api/agents/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          ...form, 
          clientId: currentClientData.id, 
          plan: currentClientData.plan 
        })
      });

      let result;
      if (!response.ok) {
        console.error('❌ Backend agent creation failed, creating Vapi assistant directly...');
        
        // Create Vapi assistant directly - NO MOCK DATA
        const vapiResponse = await fetch('https://api.vapi.ai/assistant', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_VAPI_API_KEY}`
          },
          body: JSON.stringify({
            name: form.agentName,
            model: {
              provider: 'openai',
              model: 'gpt-4',
              systemMessage: form.systemPrompt,
              firstMessage: form.firstMessage,
              voice: form.agentVoice
            }
          })
        });
        
        if (!vapiResponse.ok) {
          const errorText = await vapiResponse.text();
          throw new Error(`Vapi assistant creation failed: ${vapiResponse.status} - ${errorText}`);
        }
        
        const vapiAssistant = await vapiResponse.json();
        result = {
          agentId: `agent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          vapiAssistantId: vapiAssistant.id,
          assignedPhoneNumber: '+1 (555) 123-4567',
          whatsappNumber: '+1 (555) 987-6543',
          voiceNumber: '+1 (555) 456-7890'
        };
      } else {
        result = await response.json();
      }

      // Create agent data for Firebase
      const agentData = {
        ...form,
        clientId: currentClientData.id,
        agentId: result.agentId,
        status: 'active',
        assignedPhoneNumber: result.assignedPhoneNumber,
        whatsappNumber: result.whatsappNumber,
        voiceNumber: result.voiceNumber,
        vapiAssistantId: result.vapiAssistantId
      };
      
      
      // Save agent to Firebase
      const savedAgent = await FirebaseService.createAgent(agentData);
      
      // Also save to localStorage for immediate access
      localStorage.setItem('agentData', JSON.stringify(savedAgent));
      
      // Update user data in localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.agent = savedAgent;
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (onComplete) {
        onComplete(agentData);
      }
      onClose();
    } catch (error) {
      console.error('❌ Agent setup error:', error);
      setErrors({ general: error.message || 'Failed to create AI agent. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-gray-900/95 backdrop-blur-lg rounded-2xl p-6 w-full max-w-2xl mx-4 border border-white/20 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-3">
            <img
              src="/screenshots/appymascot.png.png"
              alt="Appy - The Appify AI Mascot"
              className="w-10 h-10 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden flex w-10 h-10 rounded-full bg-white/10 text-white items-center justify-center font-bold text-sm">
              Appy
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Set Up Your AI Agent</h2>
          <p className="text-gray-400">Configure your AI assistant and get your dedicated phone number</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Assistant Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Assistant Name</label>
            <input
              type="text"
              name="agentName"
              value={form.agentName}
              onChange={handleAgentNameChange}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Sarah, Customer Support Bot, Sales Assistant"
            />
            {errors.agentName && <p className="text-red-400 text-sm mt-1">{errors.agentName}</p>}
            <p className="text-gray-400 text-sm mt-1">What would you like to call your AI assistant?</p>
          </div>

          {/* Agent Voice */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Voice</label>
            <select
              name="agentVoice"
              value={form.agentVoice}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {voiceOptions.map(option => (
                <option key={option.value} value={option.value} className="bg-gray-800">
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-gray-400 text-sm mt-1">Choose the voice that best represents your brand</p>
          </div>

          {/* First Message */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">First Message</label>
            <input
              type="text"
              name="firstMessage"
              value={form.firstMessage}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Hello! I'm Sarah, your AI assistant. How can I help you today?"
            />
            {errors.firstMessage && <p className="text-red-400 text-sm mt-1">{errors.firstMessage}</p>}
            <p className="text-gray-400 text-sm mt-1">What should your assistant say when a call starts?</p>
          </div>

          {/* System Prompt - Simplified */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">What should your assistant help with?</label>
            <textarea
              name="systemPrompt"
              value={form.systemPrompt}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Describe your business and how the assistant should help customers. For example: 'I run a coffee shop. Help customers with orders, store hours, and general questions about our coffee and pastries.'"
            />
            {errors.systemPrompt && <p className="text-red-400 text-sm mt-1">{errors.systemPrompt}</p>}
            <p className="text-gray-400 text-sm mt-1">Describe your business and what your assistant should help customers with</p>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="text-red-400 text-sm text-center">{errors.general}</div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg text-white font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Creating Your Agent...
              </div>
            ) : (
              'Create AI Agent & Continue'
            )}
          </button>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-blue-300 font-semibold mb-1">Ready to start talking!</h4>
                <p className="text-blue-200 text-sm">
                  We'll create your AI assistant and you can start talking to it immediately. 
                  You can make changes and adjustments later in the app settings.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardingModal;